import React, { useState } from 'react';
import { Shield, ArrowRight, ArrowLeft } from 'lucide-react';
import { CertifiedNetworkApplication } from '../../services/certifiedNetworkService';
import LoadingButton from '../LoadingButton';

interface Step3ComplianceProps {
  data: Partial<CertifiedNetworkApplication>;
  onComplete: (data: any) => void;
  onBack: () => void;
}

const Step3Compliance: React.FC<Step3ComplianceProps> = ({ data, onComplete, onBack }) => {
  const [formData, setFormData] = useState({
    dpo: data.dpo || {
      nomeCompleto: '',
      isNominato: false,
      descrizioneProtezione: ''
    },
    gdpr: data.gdpr || {
      hasMisure: false,
      hasDPIA: false
    },
    insurance: data.insurance || {
      hasInsurance: false,
      isSermetra: false,
      compagnia: '',
      numeroPolizza: '',
      massimale: ''
    },
    cybersecurity: data.cybersecurity || {
      hasMisure: false,
      descrizioneSoluzioni: ''
    }
  });

  const handleChange = (section: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        [field]: value
      }
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onComplete(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/50 rounded-full mb-4">
          <Shield className="w-8 h-8 text-purple-600 dark:text-purple-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Conformità e Sicurezza
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Informazioni su DPO, GDPR, assicurazione professionale e cybersecurity.
        </p>
      </div>

      {/* Sezione 4: DPO */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
          4. Responsabile della Protezione dei Dati (DPO)
        </h3>

        <div className="flex items-center gap-3 mb-4">
          <input
            type="checkbox"
            id="isNominato"
            checked={formData.dpo.isNominato}
            onChange={(e) => handleChange('dpo', 'isNominato', e.target.checked)}
            className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
          />
          <label htmlFor="isNominato" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            È stato nominato un DPO conforme al GDPR
          </label>
        </div>

        {formData.dpo.isNominato && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nome e Cognome del DPO
              </label>
              <input
                type="text"
                value={formData.dpo.nomeCompleto}
                onChange={(e) => handleChange('dpo', 'nomeCompleto', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Descrivere brevemente le modalità di protezione dei dati
              </label>
              <textarea
                rows={3}
                value={formData.dpo.descrizioneProtezione}
                onChange={(e) => handleChange('dpo', 'descrizioneProtezione', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
              />
            </div>
          </>
        )}
      </div>

      {/* Sezione 5: GDPR */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
          5. Conformità al GDPR
        </h3>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="hasMisure"
              checked={formData.gdpr.hasMisure}
              onChange={(e) => handleChange('gdpr', 'hasMisure', e.target.checked)}
              className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
            />
            <label htmlFor="hasMisure" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Lo Studio ha adottato le misure previste dal GDPR
            </label>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="hasDPIA"
              checked={formData.gdpr.hasDPIA}
              onChange={(e) => handleChange('gdpr', 'hasDPIA', e.target.checked)}
              className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
            />
            <label htmlFor="hasDPIA" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              È stata eseguita una valutazione dei rischi per la privacy (DPIA)
            </label>
          </div>
        </div>
      </div>

      {/* Sezione 6: Assicurazione Professionale */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
          6. Assicurazione Professionale
        </h3>

        <div className="flex items-center gap-3 mb-4">
          <input
            type="checkbox"
            id="hasInsurance"
            checked={formData.insurance.hasInsurance}
            onChange={(e) => handleChange('insurance', 'hasInsurance', e.target.checked)}
            className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
          />
          <label htmlFor="hasInsurance" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Lo Studio possiede un'assicurazione professionale
          </label>
        </div>

        {!formData.insurance.hasInsurance && (
          <div className="flex items-center gap-3 mb-4">
            <input
              type="checkbox"
              id="isSermetra"
              checked={formData.insurance.isSermetra}
              onChange={(e) => handleChange('insurance', 'isSermetra', e.target.checked)}
              className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
            />
            <label htmlFor="isSermetra" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Lo Studio è consorziato Sermetra (esente da assicurazione)
            </label>
          </div>
        )}

        {formData.insurance.hasInsurance && (
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Compagnia Assicurativa
              </label>
              <input
                type="text"
                value={formData.insurance.compagnia}
                onChange={(e) => handleChange('insurance', 'compagnia', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Numero Polizza
              </label>
              <input
                type="text"
                value={formData.insurance.numeroPolizza}
                onChange={(e) => handleChange('insurance', 'numeroPolizza', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Massimale Assicurativo
              </label>
              <input
                type="text"
                value={formData.insurance.massimale}
                onChange={(e) => handleChange('insurance', 'massimale', e.target.value)}
                placeholder="es. € 500.000"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Sezione 7: Cybersecurity */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
          7. Cybersecurity
        </h3>

        <div className="flex items-center gap-3 mb-4">
          <input
            type="checkbox"
            id="hasMisureCyber"
            checked={formData.cybersecurity.hasMisure}
            onChange={(e) => handleChange('cybersecurity', 'hasMisure', e.target.checked)}
            className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
          />
          <label htmlFor="hasMisureCyber" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Lo Studio ha adottato misure di sicurezza informatica (antivirus, firewall, backup)
          </label>
        </div>

        {formData.cybersecurity.hasMisure && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Descrivere brevemente le soluzioni di sicurezza adottate
            </label>
            <textarea
              rows={4}
              value={formData.cybersecurity.descrizioneSoluzioni}
              onChange={(e) => handleChange('cybersecurity', 'descrizioneSoluzioni', e.target.value)}
              placeholder="es. Antivirus Kaspersky, Firewall hardware Cisco, Backup giornaliero su cloud..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
            />
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
          Indietro
        </button>
        <LoadingButton
          type="submit"
          className="flex items-center gap-2 bg-green-600 text-white px-8 py-2 rounded-lg hover:bg-green-700"
        >
          Continua
          <ArrowRight className="w-5 h-5" />
        </LoadingButton>
      </div>
    </form>
  );
};

export default Step3Compliance;

